import * as React from "react";
import { AlertTriangle } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";
import { Input } from "./input";
import { Label } from "./label";

export interface ConfirmDialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Element that opens the dialog (Radix Trigger via asChild). */
  trigger?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as a destructive action. Default true. */
  danger?: boolean;
  /**
   * Dangerous production actions: the user must type this exact phrase
   * (case-sensitive) before confirming.
   */
  requireTextMatch?: string;
  /** Label for the phrase input. Defaults to "Type {requireTextMatch} to confirm". */
  matchLabel?: React.ReactNode;
  /** Called on confirm. May return a promise — the dialog shows a spinner while pending. */
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  /** Extra content (impact summary, affected resources). */
  children?: React.ReactNode;
}

const ConfirmDialog = React.forwardRef<HTMLDivElement, ConfirmDialogProps>(
  (
    {
      open,
      defaultOpen,
      onOpenChange,
      trigger,
      title,
      description,
      confirmLabel = "Confirm",
      cancelLabel = "Cancel",
      danger = true,
      requireTextMatch,
      matchLabel,
      onConfirm,
      onCancel,
      children,
      ...aria
    },
    ref,
  ) => {
    const [text, setText] = React.useState("");
    const [pending, setPending] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const inputId = React.useId();

    React.useEffect(() => {
      if (open === false || open === undefined) {
        // Reset when the dialog is closed.
        setText("");
        setError(null);
        setPending(false);
      }
    }, [open]);

    const matchOk = !requireTextMatch || text === requireTextMatch;

    const handleConfirm = async () => {
      if (requireTextMatch && text !== requireTextMatch) {
        setError(`Type “${requireTextMatch}” exactly to enable this action.`);
        return;
      }
      setError(null);
      try {
        setPending(true);
        await onConfirm?.();
        onOpenChange?.(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : "The action failed. Try again.");
      } finally {
        setPending(false);
      }
    };

    return (
      <Dialog
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={(next) => {
          onOpenChange?.(next);
          if (!next) {
            setText("");
            setError(null);
            onCancel?.();
          }
        }}
      >
        {trigger ? (
          <DialogTrigger asChild={React.isValidElement(trigger)}>
            {trigger}
          </DialogTrigger>
        ) : null}
        <DialogContent ref={ref} className="max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3">
              {danger ? (
                <span
                  aria-hidden="true"
                  className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive"
                >
                  <AlertTriangle className="size-5" />
                </span>
              ) : null}
              <div className="flex flex-col gap-1.5">
                <DialogTitle className={cn(danger && "text-foreground")}>{title}</DialogTitle>
                {description ? (
                  <DialogDescription>{description}</DialogDescription>
                ) : (
                  <DialogDescription className="sr-only">
                    Please confirm or cancel this action.
                  </DialogDescription>
                )}
              </div>
            </div>
          </DialogHeader>
          {children}
          {requireTextMatch ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor={inputId} className="font-mono text-xs">
                {matchLabel ?? (
                  <>
                    Type <span className="rounded bg-muted px-1 py-0.5 font-mono">{requireTextMatch}</span> to
                    confirm
                  </>
                )}
              </Label>
              <Input
                id={inputId}
                value={text}
                autoComplete="off"
                spellCheck={false}
                aria-invalid={Boolean(error) || undefined}
                onChange={(e) => {
                  setText(e.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && matchOk && !pending) void handleConfirm();
                }}
                placeholder={requireTextMatch}
                autoFocus
              />
            </div>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={pending}>
              {cancelLabel}
            </Button>
            <Button
              variant={danger ? "destructive" : "primary"}
              onClick={() => void handleConfirm()}
              loading={pending}
              disabled={!matchOk}
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);
ConfirmDialog.displayName = "ConfirmDialog";

export interface ConfirmOptions
  extends Omit<ConfirmDialogProps, "open" | "onOpenChange" | "trigger" | "onCancel"> {}

/**
 * Promise-flavoured confirm: `const ok = await confirmDialog(opts)` — renders a
 * controlled ConfirmDialog for the host. Keeps the API callback-free when desired.
 */
export function useConfirmDialog(): {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  dialog: React.ReactElement;
} {
  const [options, setOptions] = React.useState<ConfirmOptions | null>(null);
  const [open, setOpen] = React.useState(false);
  const resolver = React.useRef<((ok: boolean) => void) | null>(null);

  const confirm = React.useCallback((opts: ConfirmOptions) => {
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (ok: boolean) => {
    setOpen(false);
    resolver.current?.(ok);
    resolver.current = null;
  };

  const dialog = (
    <ConfirmDialog
      {...(options ?? { title: "" })}
      open={open}
      onOpenChange={(next) => {
        if (!next) settle(false);
      }}
      onConfirm={async () => {
        await options?.onConfirm?.();
      }}
      onCancel={() => settle(false)}
    />
  );

  // Wrap confirm so the promise resolves after the callback succeeds.
  const confirmWrapped = React.useCallback(
    (opts: ConfirmOptions) => {
      const inner = opts.onConfirm;
      const promise = confirm({
        ...opts,
        onConfirm: async () => {
          await inner?.();
          settle(true);
        },
      });
      return promise;
    },
    [confirm],
  );

  return { confirm: confirmWrapped, dialog };
}

export { ConfirmDialog };
